import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSystemWorkspaces } from './useSystemWorkspaces';
import {
  generateSystemWorkspacePreferenceKey,
  getSystemWorkspaceIdFromPreferenceKey,
  getSystemWorkspacePreferenceKey,
} from './utils';
import { Workspace } from './WorkspaceContext';
import { useHistory, useLocation } from 'react-router-dom';
import { WORKSPACES_PAGE_ROUTE } from 'const';

export const useSystemWorkspacesMenu = () => {
  const { t } = useTranslation('workspace');
  const { systemWorkspaces, loading, setPreference, deletePreference } = useSystemWorkspaces();
  const { push, replace } = useHistory();
  const { pathname } = useLocation();

  const addWorkspace = useCallback(
    (defaults: Partial<Workspace> = {}) => {
      const preferenceKey = defaults.id
        ? getSystemWorkspacePreferenceKey(defaults.id)
        : generateSystemWorkspacePreferenceKey();

      const newWorkspace: Workspace = {
        id: getSystemWorkspaceIdFromPreferenceKey(preferenceKey),
        layout: {},
        title: t('defaultSystemWorkspaceName', { number: systemWorkspaces.length + 1 }),
        gridItemData: {},
        ...defaults,
        isSystem: true,
      };
      return setPreference(preferenceKey, newWorkspace).then(() => {
        push(`/${WORKSPACES_PAGE_ROUTE}/${newWorkspace.id}`);
      });
    },
    [t, systemWorkspaces.length, setPreference, push]
  );

  const deleteWorkspace = useCallback(
    (workspaceId: string) => {
      return deletePreference(getSystemWorkspacePreferenceKey(workspaceId)).then(() => {
        if (pathname.includes(workspaceId)) {
          replace('/');
        }
      });
    },
    [deletePreference, pathname, replace]
  );

  return {
    systemWorkspaces,
    loading,
    addWorkspace,
    deleteWorkspace,
  };
};
